import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  postProductCompatibility,
  getCompatPrefs,
  type ProductCompatResponse,
  type ProductPairIds,
} from '../api/endpoints';
import { isIngredientsPendingError, isSameProductError } from '../api/pairErrors';
import { isCreditError } from '../api/config';
import useScanCredits from '../hooks/useScanCredits';
import { IconHelp, IconSwap, IconGear, IconLink, IconSpark } from '../components/icons/AnalyzerIcons';
import WaitingGame from '../components/WaitingGame';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';
import {
  areSameProductReferences,
  createProductReference,
  readCompatibilityDraft,
  writeCompatibilityDraft,
  type CompatibilityUsage,
  type ProductPairTransfer,
} from '../utils/productIdentity';
import CompatPreferencesPage from './shared/CompatPreferencesPage';
import UsageSegmented from './shared/UsageSegmented';
import ProductSideFlow from './shared/productSideFlow/ProductSideFlow';
import { NOT_ENOUGH_CREDITS_MESSAGE } from './shared/productSideFlow/useProductSideFlow';
import { pendingBarcode, referenceFromSide, sideFromReference } from './shared/productSideFlow/sideProducts';
import { CreditPill, OutOfCreditsCard, usePairSelection, type PairSide } from './shared/pairScreen';
import { AnimatedSwap, pageVariants, sectionItem, spring } from './shared/screenMotion';

export type { CompatibilityUsage } from '../utils/productIdentity';

export interface ProductCompatInput {
  products?: ProductPairTransfer['products'];
  prefill?: { barcodeA?: string; barcodeB?: string; productIdA?: number; productIdB?: number };
  usage?: { a?: CompatibilityUsage; b?: CompatibilityUsage };
  /** Set by the result page after a 409: that side's ingredients are pending. */
  pendingSide?: PairSide;
}

export type ProductCompatPayloadV2 = ProductPairIds & { context: string };

export interface ProductCompatResult {
  response: ProductCompatResponse;
  payload: ProductCompatPayloadV2;
  productIds: { a?: number; b?: number };
}

const USAGE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'leave_on', label: 'Leave-on' },
  { value: 'leave_off', label: 'Leave-off' },
];

/** The request field `context` (leaveon_leaveon, …) from the two usages. */
export function usageContext(usageA: CompatibilityUsage, usageB: CompatibilityUsage): string {
  if (usageA === 'auto' || usageB === 'auto') return 'auto';
  const map = { leave_on: 'leaveon', leave_off: 'leaveoff' } as const;
  return `${map[usageA]}_${map[usageB]}`;
}

function initialState(input: ProductCompatInput | undefined, mode: 'page' | 'modal') {
  const prefill = input?.prefill ?? {};
  const draft = mode === 'page' ? readCompatibilityDraft() : null;
  const fromPrefill = (productId?: number, barcode?: string) =>
    productId || barcode ? createProductReference({ productId, barcode }) : undefined;
  const a = input?.products?.[0] ?? fromPrefill(prefill.productIdA, prefill.barcodeA) ?? draft?.products[0];
  const b = input?.products?.[1] ?? fromPrefill(prefill.productIdB, prefill.barcodeB) ?? draft?.products[1];
  const draftUsageA = areSameProductReferences(a, draft?.products[0]) ? draft?.usage.a : undefined;
  const draftUsageB = areSameProductReferences(b, draft?.products[1]) ? draft?.usage.b : undefined;
  return {
    initialA: sideFromReference(a, draft?.products[0]),
    initialB: sideFromReference(b, draft?.products[1]),
    barcodeA: pendingBarcode(a),
    barcodeB: pendingBarcode(b),
    usageA: input?.usage?.a ?? draftUsageA ?? 'auto',
    usageB: input?.usage?.b ?? draftUsageB ?? 'auto',
  };
}

function usePrefsPrompt(mode: 'page' | 'modal', open: () => void) {
  useEffect(() => {
    if (mode !== 'page') return;
    void (async () => {
      try {
        const p = await getCompatPrefs();
        const looksDefault =
          p.allergies.length === 0 &&
          p.sensitivity === 'likely' &&
          !p.acne_prone &&
          p.ethical_mode === 'off' &&
          !p.cautious_actives;
        const shown = localStorage.getItem('compatPrefsPromptShown');
        if (looksDefault && !shown) {
          localStorage.setItem('compatPrefsPromptShown', '1');
          open();
        }
      } catch { /* ignore */ }
    })();
  }, [mode, open]);
}

const ProductCompatScreen: React.FC<ScreenProps<ProductCompatInput, ProductCompatResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const nav = useNavigate();
  const [initial] = useState(() => initialState(input, mode));
  const pair = usePairSelection(initial.initialA, initial.initialB, input?.pendingSide);
  const { productA, productB } = pair;
  const [usageA, setUsageA] = useState<CompatibilityUsage>(initial.usageA);
  const [usageB, setUsageB] = useState<CompatibilityUsage>(initial.usageB);
  const context = useMemo(() => usageContext(usageA, usageB), [usageA, usageB]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [gameVisible, setGameVisible] = useState(false);
  const gameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openPrefs = useCallback(() => { setShowPrefs(true); }, []);
  usePrefsPrompt(mode, openPrefs);

  const { total: creditsLeft, cost, outOfQuota, refreshCredits } = useScanCredits('product_compat');

  useEffect(() => () => { if (gameTimerRef.current) clearTimeout(gameTimerRef.current); }, []);

  useEffect(() => {
    if (mode !== 'page') return;
    writeCompatibilityDraft([referenceFromSide(productA), referenceFromSide(productB)], { a: usageA, b: usageB });
  }, [mode, productA, productB, usageA, usageB]);

  const handleSubmit = async () => {
    if (!pair.canSubmit || !productA || !productB || loading || outOfQuota) return;
    setLoading(true);
    setError(null);
    if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
    const timer = setTimeout(() => { setGameVisible(true); }, 600);
    gameTimerRef.current = timer;

    const payload: ProductCompatPayloadV2 = { product_id_a: productA.productId, product_id_b: productB.productId, context };
    try {
      const response = await postProductCompatibility(payload);
      void refreshCredits();
      const productIds = { a: productA.productId, b: productB.productId };
      setGameVisible(false);
      setLoading(false);
      if (mode === 'modal') onComplete?.({ response, payload, productIds });
      else nav('/compatibility/result', { state: { response, payload, productIds } });
    } catch (error_: unknown) {
      if (isIngredientsPendingError(error_)) {
        // The side itself says its ingredients are pending; no second message here.
        pair.markPending(error_.side);
      } else if (isSameProductError(error_)) {
        setError('Choose two different products to check compatibility.');
      } else if (isCreditError(error_)) {
        setError(error_.status === 402 ? NOT_ENOUGH_CREDITS_MESSAGE : error_.message);
        void refreshCredits();
      } else {
        setError(getErrorMessage(error_, 'Failed to check compatibility'));
      }
      setGameVisible(false);
      setLoading(false);
    } finally {
      clearTimeout(timer);
    }
  };

  const prefersReduced = useReducedMotion() ?? false;
  const [swapAngle, setSwapAngle] = useState(0);
  const handleSwap = () => {
    if (!pair.canSwap) return;
    pair.swap();
    // Usage stays with each product.
    setUsageA(usageB);
    setUsageB(usageA);
    setSwapAngle((a) => a + 180);
  };

  /** The pair is complete and affordable: the submit button is live. */
  const canRun = pair.canSubmit && !outOfQuota;
  const notice = error ?? (pair.duplicate ? 'Choose two different products to check compatibility.' : null);
  const usageControl = (side: PairSide) => (
    <UsageSegmented
      side={side}
      label="Usage"
      value={side === 'a' ? usageA : usageB}
      onChange={(v) => { (side === 'a' ? setUsageA : setUsageB)(v as CompatibilityUsage); }}
      options={USAGE_OPTIONS}
    />
  );
  const sideFlow = (side: PairSide) => (
    <ProductSideFlow
      key={`${side}-${pair.sideKey}`}
      side={side}
      context="compat"
      label={side === 'a' ? 'Product A' : 'Product B'}
      initial={side === 'a' ? pair.initialA : pair.initialB}
      initialBarcode={pair.sideKey === 0 ? (side === 'a' ? initial.barcodeA : initial.barcodeB) : undefined}
      disabledProductId={(side === 'a' ? productB : productA)?.productId}
      pendingMarked={pair.isPending(side)}
      onChange={pair.onChange(side)}
      footer={usageControl(side)}
    />
  );
  const cta = <><IconSpark /> <AnimatedSwap value={loading ? 'Checking…' : 'Run compatibility'} /></>;

  const inner = (
    <AnimatePresence mode="wait" initial={false}>
      {showPrefs ? (
        <motion.div
          key="prefs"
          initial={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          transition={spring}
        >
          <CompatPreferencesPage onBack={() => { setShowPrefs(false); }} />
        </motion.div>
      ) : (
        <motion.div key="main" className="stack-4" variants={pageVariants} initial="hidden" animate="visible">
          <motion.div variants={sectionItem} className="card card--bordered relative overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(135deg, var(--c-teal-weak) 0%, transparent 50%, var(--c-teal-weak) 100%)' }}
            />
            <div className="relative">
              <div className="row-3 mb-3">
                <div
                  className="grid place-items-center w-9 h-9 rounded-full shrink-0"
                  style={{ background: 'var(--c-teal-weak)', color: 'var(--c-kivo-teal)' }}
                >
                  <IconLink />
                </div>
                <CreditPill cost={cost} left={creditsLeft} outOfQuota={outOfQuota} />
                <button type="button" onClick={() => { setShowPrefs(true); }} className="btn btn--ghost btn--sm ml-auto row-3">
                  <IconGear /> Preferences
                </button>
              </div>
              <h1 className="t-h3">Product compatibility checker</h1>
              <p className="t-small t-mute mt-1">
                Scan, photograph or pick two products. <strong>Product A first</strong>, then B. Use <em>Swap</em> to flip.
                Each card has a <strong>Usage</strong> control — <em>Leave-on</em> (stays on skin, e.g. serum) vs <em>Leave-off</em> (rinses off, e.g. cleanser), or keep <em>Auto</em>.
              </p>
              {mode === 'page' && (
                <button
                  type="button"
                  onClick={() => { nav('/scanner/how-it-works'); }}
                  className="mt-2 inline-flex items-center gap-1 t-small"
                  style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline' }}
                >
                  <IconHelp /> How this works
                </button>
              )}
            </div>
          </motion.div>

          <motion.div variants={sectionItem} className="card card--bordered stack-4">
            {/* The sides stay mounted when credits run out, so a charged scan is never lost. */}
            <>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:gap-5">
                  {sideFlow('a')}
                  <div className="flex items-center justify-center">
                    <div className="stack-2 items-center w-full xl:w-auto">
                      <motion.button
                        type="button"
                        whileTap={{ scale: 0.96 }}
                        onClick={handleSwap}
                        disabled={!pair.canSwap || loading}
                        className="btn btn--ghost btn--sm row-3 w-full xl:w-auto justify-center"
                      >
                        <motion.span animate={{ rotate: swapAngle }} transition={{ type: 'spring', stiffness: 320, damping: 28 }} style={{ display: 'inline-flex' }}>
                          <IconSwap />
                        </motion.span>
                        Swap A/B
                      </motion.button>
                      <p className="t-small t-mute text-center">Usage stays with each product</p>
                    </div>
                  </div>
                  {sideFlow('b')}
                </div>
                {notice && (
                  <p
                    className="t-small"
                    role="alert"
                    style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                  >
                    {notice}
                  </p>
                )}
                {/* Only a ready pair can be short of credits: the pill covers the rest. */}
                {pair.canSubmit && outOfQuota && (
                  <OutOfCreditsCard
                    closeLabel={mode === 'modal' ? 'Close' : 'Back'}
                    onBuy={() => { nav('/wallet'); }}
                    onClose={() => { if (mode === 'modal') onCancel?.(); else nav('/'); }}
                  />
                )}
                {canRun && (
                  <div className="compare-ready" role="status">
                    <span className="dot" /> Ready to check compatibility
                  </div>
                )}
                <div className={mode === 'page' ? 'hidden md:flex md:flex-wrap gap-2' : 'flex flex-col sm:flex-row sm:flex-wrap gap-2'}>
                  <motion.button
                    type="button"
                    whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                    onClick={handleSubmit}
                    disabled={!pair.canSubmit || loading || outOfQuota}
                    className="btn btn--primary row-3 w-full sm:w-auto justify-center"
                  >
                    {cta}
                  </motion.button>
                </div>
            </>
          </motion.div>

          {mode === 'page' && (
            <div className="compare-mobile-cta md:hidden" data-testid="compat-mobile-cta">
              <div className="t-small" style={{ fontWeight: 600, color: canRun ? 'var(--c-deep-teal)' : 'var(--ink-muted)' }}>
                {canRun ? 'Ready to check compatibility' : pair.canSubmit ? 'Top up to continue' : 'Select two different products'}
              </div>
              <button type="button" onClick={handleSubmit} disabled={!pair.canSubmit || loading || outOfQuota} className="btn btn--primary row-3 w-full justify-center">
                {cta}
              </button>
            </div>
          )}

          {gameVisible && loading && <WaitingGame onExit={() => { setGameVisible(false); }} />}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (mode === 'modal') {
    return (
      <ModalShell title="Product compatibility" onClose={() => onCancel?.()} widthClass="max-w-4xl">
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default ProductCompatScreen;
